import React, { useState, useEffect } from 'react'
import { Modal, Button, Table, Spinner, Alert } from 'react-bootstrap'
import { Download, FileText } from 'lucide-react'
import { caseDocumentDownload } from '../../../API/litigationApis' // Adjust the import path as needed
import { showToast } from "../../../utils/handleToast"

const ViewDocumentsModal = ({ showDocumentsModal, setShowDocumentsModal, selectedCase }) => {
    const [documents, setDocuments] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [downloadingIds, setDownloadingIds] = useState(new Set())

    // Fetch documents when modal opens
    useEffect(() => {
        if (showDocumentsModal && selectedCase?.case_id) {
            fetchDocuments()
        }
    }, [showDocumentsModal, selectedCase])

    const fetchDocuments = async () => {
        try {
            setLoading(true)
            setError(null)
            
            const params = {
                case_id: selectedCase.case_id
            }
            
            const response = await caseDocumentDownload(params)
            
            if (response?.status && response?.data) {
                setDocuments(response.data.results || [])
            } else {
                throw new Error(response?.message || "Failed to fetch documents")
            }
        } catch (error) {
            console.error("Error fetching documents:", error)
            setError(error.message || "Failed to fetch documents")
            showToast({
                variant: "error",
                message: error.message || "Failed to fetch documents"
            })
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setShowDocumentsModal(false)
        setDocuments([])
        setError(null)
        setDownloadingIds(new Set())
    }

    const handleDownload = async (document) => {
        if (!document.document_url) {
            showToast({
                variant: "warning",
                message: "Document URL not available"
            })
            return
        }

        try {
            // Add to downloading set
            setDownloadingIds(prev => new Set([...prev, document.id]))
            
            const downloadUrl = `https://api.themedius.ai/dashboard/api/download_media/?url=${encodeURIComponent(document.document_url)}`
            
            window.open(downloadUrl, '_blank')

            showToast({
                variant: "success",
                message: "Document download started"
            })
        } catch (error) {
            console.error("Error downloading document:", error)
            showToast({
                variant: "error",
                message: "Failed to download document"
            })
        } finally {
            // Remove from downloading set after a short delay
            setTimeout(() => {
                setDownloadingIds(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(document.id)
                    return newSet
                })
            }, 2000)
        }
    }

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '--'
        try {
            const date = new Date(dateString)
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        } catch (err) {
            return '--'
        }
    }

    // Format document type for display
    const formatDocumentType = (type) => {
        if (!type) return 'Unknown'
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    // Get file name from URL
    const getFileName = (url) => {
        if (!url) return 'Document'
        try {
            const urlParts = url.split('/')
            return urlParts[urlParts.length - 1] || 'Document'
        } catch {
            return 'Document'
        }
    }

    return (
        <Modal 
            show={showDocumentsModal} 
            onHide={handleClose}
            size="xl"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Case Documents
                    {selectedCase && (
                        <div className="text-muted fs-6 mt-1">
                            {selectedCase.customer_name} ({selectedCase.lan_number})
                        </div>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">Loading documents...</p>
                    </div>
                ) : error ? (
                    <Alert variant="danger">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error}</p>
                        <Button variant="outline-danger" size="sm" onClick={fetchDocuments}>
                            Try Again
                        </Button>
                    </Alert>
                ) : documents.length === 0 ? (
                    <div className="text-center py-4">
                        <FileText size={48} className="text-muted mb-3" />
                        <p className="text-muted">No documents found for this case.</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <div className="row">
                                <div className="col-md-6">
                                    <small className="text-muted">Total Documents:</small>
                                    <div className="fw-bold fs-5">
                                        {documents.length}
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <small className="text-muted">
                                        Case ID: {selectedCase?.case_id}
                                    </small>
                                </div>
                            </div>
                        </div>
                        
                        <div className="table-responsive">
                            <Table striped hover>
                                <thead className="table-gray">
                                    <tr>
                                        <th>S.No.</th>
                                        <th>Document Type</th>
                                        <th>File Name</th>
                                        <th>Upload Date</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents.map((document, index) => (
                                        <tr key={document.id}>
                                            <td style={{paddingLeft:"-20px"}}>{index + 1}</td>
                                            <td style={{paddingLeft:"-20px"}}>
                                                <span className="text-capitalize">
                                                    {formatDocumentType(document.document_type)}
                                                </span>
                                            </td>
                                            <td style={{paddingLeft:"-20px"}}>
                                                <div className="d-flex align-items-start">
                                                    <FileText size={16} className="me-2 text-muted" />
                                                    <span className="text-truncate" style={{ maxWidth: '200px' }} title={getFileName(document.document_url)}>
                                                        {getFileName(document.document_url)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{paddingLeft:"-20px"}}>
                                                <small className="text-muted">
                                                    {formatDate(document.created_at)}
                                                </small>
                                            </td>
                                            <td style={{paddingLeft:"-20px"}}>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleDownload(document)}
                                                    disabled={downloadingIds.has(document.id) || !document.document_url}
                                                    className="d-flex align-items-center"
                                                >
                                                    {downloadingIds.has(document.id) ? (
                                                        <>
                                                            <Spinner 
                                                                as="span" 
                                                                animation="border" 
                                                                size="sm" 
                                                                className="me-1" 
                                                            />
                                                            Downloading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Download size={14} className="me-1" />
                                                            Download
                                                        </>
                                                    )}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                {/* {documents.length > 0 && (
                    <Button variant="outline-primary" onClick={fetchDocuments}>
                        Refresh
                    </Button>
                )} */}
                {documents.length > 0 && (
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            // Download all documents
                            documents.forEach((doc, index) => {
                                setTimeout(() => handleDownload(doc), index * 500) // Stagger downloads
                            })
                        }}
                        disabled={downloadingIds.size > 0}
                    >
                        Download All
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default ViewDocumentsModal